import React, { useCallback, useEffect, useRef } from 'react';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { useMapPanelContext } from '../MapPanelContext';

interface ResizableTableProps {
  className?: string;
  children: React.ReactNode;
}

export const ResizableTable: React.FC<ResizableTableProps> = ({
  className,
  children,
}) => {
  const { setTableHeight } = useMapPanelContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef<number>(0);

  // Handle manual resize via the resize handle
  const handleResize = useCallback(
    (sizes: number[]) => {
      // Get the container's total height
      const containerHeight = containerRef.current?.clientHeight || 0;

      // Calculate the actual pixel height of the second panel
      const actualHeight = (sizes[1] / 100) * containerHeight;
      setTableHeight(actualHeight);
    },
    [setTableHeight]
  );

  useEffect(() => {
    // Set initial height
    const containerHeight = containerRef.current?.clientHeight || 0;
    const actualHeight = 0.5 * containerHeight;
    setTableHeight(actualHeight);
  }, [setTableHeight]);

  // Periodically check for container height changes
  useEffect(() => {
    // Initial height setup
    if (containerRef.current) {
      lastHeightRef.current = containerRef.current.clientHeight;
    }

    // Set up periodic check
    const intervalId = setInterval(() => {
      if (containerRef.current) {
        const currentHeight = containerRef.current.clientHeight;

        // If height has changed significantly
        if (currentHeight === lastHeightRef.current) {
          return;
        }

        if (!childrenRef.current) {
          return;
        }

        lastHeightRef.current = currentHeight;

        const childrenHeight = childrenRef.current.clientHeight;
        setTableHeight(childrenHeight);
      }
    }, 250);

    return () => clearInterval(intervalId);
  }, [setTableHeight]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'h-[calc(100%-218px)] w-full rounded-lg overflow-hidden pointer-events-none',
        className
      )}
    >
      <ResizablePanelGroup
        direction="vertical"
        onLayout={handleResize}
        className={
          'flex flex-col h-full w-full overflow-hidden border-0 pointer-events-none'
        }
      >
        <ResizablePanel
          className="flex-1 pointer-events-none"
          style={{ background: 'transparent' }}
        />

        <ResizableHandle
          withHandle
          className="mt-2 bg-transparent pointer-events-auto"
        />

        <ResizablePanel
          className="overflow-auto pointer-events-auto"
          defaultSize={50}
          minSize={25}
        >
          <div ref={childrenRef} className="w-full h-full">
            {children}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};

ResizableTable.displayName = 'ResizableTable';
